// Moteur de rapports partagé par les espaces Propriétaire et Agence.
// Chaque espace fournit son catalogue de rapports, ses options de filtres et
// une fonction de construction qui transforme les données réellement affichées
// (déjà filtrées) en un rapport prêt à prévisualiser et à exporter.
// Les données proviennent exclusivement des stores de l'espace actif : le jour
// où la base de données est branchée, seuls les stores changent, pas ce moteur.

export interface ReportColumn {
  /** Clé technique utilisée dans les lignes du rapport. */
  key: string;
  /** En-tête affiché dans l'aperçu et dans les exports. */
  header: string;
  /** Alignement des valeurs (utile pour les montants). */
  align?: "left" | "right";
}

export type ReportRow = Record<string, string | number>;

export type ReportTone = "primary" | "accent" | "secondary" | "neutral";

export interface ReportTotal {
  label: string;
  value: string;
  tone?: ReportTone;
}

export interface ReportResult {
  /** Titre du rapport (type sélectionné). */
  title: string;
  /** Description courte du rapport généré. */
  description: string;
  columns: ReportColumn[];
  rows: ReportRow[];
  totals: ReportTotal[];
  /** Note optionnelle affichée sous le tableau. */
  note?: string;
}

export interface ReportTypeDef {
  id: string;
  label: string;
  icon: string;
  description: string;
}

export interface ReportFilters {
  /** Date de début (YYYY-MM-DD), vide = pas de borne. */
  dateFrom: string;
  /** Date de fin (YYYY-MM-DD), vide = pas de borne. */
  dateTo: string;
  immeubleId: string;
  bienId: string;
  locataireId: string;
  proprietaireId: string;
  /** Valeur du filtre contextuel (statut, type, catégorie... selon le rapport). */
  statut: string;
}

export interface ReportOption {
  value: string;
  label: string;
}

export interface ImmeubleOption extends ReportOption {
  proprietaireId?: string;
}

export interface BienOption extends ReportOption {
  immeubleId: string;
  proprietaireId?: string;
}

export interface LocataireOption extends ReportOption {
  proprietaireId?: string;
}

export type ProprietaireOption = ReportOption;

export interface ReportOptions {
  immeubles: ImmeubleOption[];
  biens: BienOption[];
  locataires: LocataireOption[];
  /** Présent uniquement pour l'espace Agence. */
  proprietaires?: ProprietaireOption[];
}

export interface StatutFilterConfig {
  /** Libellé du filtre contextuel (Statut / Type / Catégorie...). */
  label: string;
  options: ReportOption[];
}

export const DEFAULT_STATUT_CONFIG: StatutFilterConfig = { label: "Statut", options: [] };

export interface ReportsWorkspaceProps {
  heading: string;
  intro: string;
  /** Catalogue des rapports disponibles pour l'espace. */
  reportTypes: ReportTypeDef[];
  /** Options de filtres (immeubles, biens, locataires, propriétaires). */
  options: ReportOptions;
  /** Filtre contextuel (statut/type/catégorie) propre au rapport sélectionné. */
  getStatutConfig?: (typeId: string) => StatutFilterConfig;
  /** Construit le rapport à partir du type et des filtres sélectionnés. */
  buildReport: (typeId: string, filters: ReportFilters) => ReportResult;
  /** Préfixe des fichiers exportés (ex. « rapports-proprietaire »). */
  filenamePrefix: string;
}